import type { useAppDispatch } from '../grid/reduxStore/Store';
import type { ErrorCode } from '../../../services/socket/SocketEvents';
import { deleteSection, insertImportedSections } from '../grid/reduxStore/editorSlice';
import { deleteBlockSettings, loadImportedSectionBlockStyles } from '../grid/reduxStore/blockStyleSettingsSlice';
import { setSaveStatus, setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice';
import { DocumentSaveStatus } from '../shared/models/DocumentSaveStatus';
import type { UndoRedoCommandInterface } from './undoRedoCommandVersion';
import { deleteSectionViaSocket, importSectionsViaSocket } from '../grid/reduxStore/saveHandlers';
import type { SectionContentType } from '../Sections/Section.model.ts';

export interface ImportSectionsCommandDependencies {
  dispatch: ReturnType<typeof useAppDispatch>;
  socketClient: any;
  checkDocumentLockStatus: (errorCode: ErrorCode) => boolean;
}

export interface ImportSectionsCommandParams {
  sourceContentIds: string[];
  afterSectionId?: string;
}

export class ImportSectionsCommand implements UndoRedoCommandInterface {
  readonly scope = 'section' as const;
  readonly timestamp: string;

  private readonly dispatch: ReturnType<typeof useAppDispatch>;
  private readonly socketClient: any;
  private readonly checkDocumentLockStatus: (errorCode: ErrorCode) => boolean;

  private readonly sourceContentIds: string[];
  private readonly afterSectionId: string | undefined;
  private importedSections: SectionContentType[];

  constructor(dependencies: ImportSectionsCommandDependencies, params: ImportSectionsCommandParams) {
    this.timestamp = new Date().toISOString();
    this.dispatch = dependencies.dispatch;
    this.socketClient = dependencies.socketClient;
    this.checkDocumentLockStatus = dependencies.checkDocumentLockStatus;
    this.sourceContentIds = params.sourceContentIds;
    this.afterSectionId = params.afterSectionId;
    this.importedSections = [];
  }

  async execute(): Promise<string[] | null> {
    try {
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      const response = await importSectionsViaSocket(this.socketClient, {
        sourceContentIds: this.sourceContentIds,
        afterSectionId: this.afterSectionId,
      });

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return null;
      }

      const { sections } = response.content;
      this.importedSections = sections;
      this.dispatch(insertImportedSections({ sections, afterSectionId: this.afterSectionId }));
      this.dispatch(loadImportedSectionBlockStyles({ sections }));
      this.dispatch(setSaveStatusBasedOnApiResponse({ status: response.status, errorCode: response.errorCode }));

      return sections.map((s) => s.id);
    } catch (error) {
      console.error('ImportSectionsCommand: execute failed', error);
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
      return null;
    }
  }

  async undo(): Promise<boolean> {
    if (this.importedSections.length === 0) {
      console.warn('ImportSectionsCommand: cannot undo, no imported sections recorded');
      return false;
    }

    try {
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      let allSucceeded = true;
      for (const section of [...this.importedSections].reverse()) {
        try {
          const response = await deleteSectionViaSocket(this.socketClient, { sectionId: section.id });

          if (this.checkDocumentLockStatus(response.errorCode)) {
            this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
            return false;
          }

          this.dispatch(deleteSection({ sectionId: section.id }));
          section.blocks.forEach((block) => {
            if (block.blockId) this.dispatch(deleteBlockSettings({ blockId: block.blockId }));
          });
        } catch {
          console.error(`ImportSectionsCommand: failed to delete section ${section.id} during undo`);
          allSucceeded = false;
        }
      }

      this.dispatch(setSaveStatus({ status: allSucceeded ? DocumentSaveStatus.SAVED : DocumentSaveStatus.NOT_SAVED }));
      return allSucceeded;
    } catch (error) {
      console.error('ImportSectionsCommand: undo failed', error);
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
      return false;
    }
  }

  async redo(): Promise<boolean> {
    try {
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      const response = await importSectionsViaSocket(this.socketClient, {
        sourceContentIds: this.sourceContentIds,
        afterSectionId: this.afterSectionId,
      });

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return false;
      }

      const { sections } = response.content;
      this.importedSections = sections;
      this.dispatch(insertImportedSections({ sections, afterSectionId: this.afterSectionId }));
      this.dispatch(loadImportedSectionBlockStyles({ sections }));
      this.dispatch(setSaveStatusBasedOnApiResponse({ status: response.status, errorCode: response.errorCode }));

      return true;
    } catch (error) {
      console.error('ImportSectionsCommand: redo failed', error);
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
      return false;
    }
  }

  getImportedSectionIds(): string[] {
    return this.importedSections.map((s) => s.id);
  }

  toJSON(): Record<string, any> {
    return {
      sourceContentIds: this.sourceContentIds,
      afterSectionId: this.afterSectionId,
      importedSectionIds: this.importedSections.map((s) => s.id),
    };
  }
}
